import i18n from "../../i18n";

/**
 * What an onboarding request holds, and what to call each part of it.
 *
 * The candidate fills everything in SUBMITTED_FIELDS from outside the product;
 * the reviewer answers the rest on the detail screen. Both lists are the
 * update endpoint's own body, in the order a person reads them.
 */

/** Filled by the candidate, and correctable by the reviewer. */
export const SUBMITTED_FIELDS = [
  "firstName",
  "lastName",
  "dateOfBirth",
  "email",
  "phoneNumber",
  "hno",
  "streetName",
  "area",
  "district",
  "state",
  "pincode",
  "verificationCode",
];

/**
 * Labels.
 *
 * The onboarding namespace already names the employee vocabulary in all four
 * languages, so a field reuses that label wherever the request spells the
 * field differently from the internal forms (hno / houseNo, area /
 * areaLocation). Anything unnamed falls back to its own key made readable.
 */
const LABEL_KEYS = {
  firstName: "onboarding.labels.firstName",
  lastName: "onboarding.labels.lastName",
  dateOfBirth: "onboarding.labels.dateOfBirth",
  email: "common.email",
  phoneNumber: "onboarding.labels.phoneNumber",
  hno: "onboarding.labels.houseNo",
  streetName: "onboarding.labels.streetName",
  area: "onboarding.labels.areaLocation",
  district: "onboarding.labels.district",
  state: "onboarding.labels.state",
  pincode: "onboarding.labels.pincode",
  verificationCode: "onboardingRequests.verificationCode",
};

function humanise(key) {
  return String(key ?? "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (first) => first.toUpperCase());
}

export function fieldLabel(key) {
  const translationKey = LABEL_KEYS[key] ?? `onboarding.labels.${key}`;
  const label = i18n.t(translationKey);
  return label === translationKey ? humanise(key) : label;
}

/** The person the request is about. */
export function requestName(request) {
  const full = [request?.firstName, request?.lastName]
    .filter((part) => part !== null && part !== undefined && String(part).trim() !== "")
    .join(" ")
    .trim();
  return full || request?.email || "";
}

/** The code the candidate is told to quote. */
export function verificationCodeOf(request) {
  return request?.verificationCode ?? "";
}

/** The status of a request, whichever name the response gives the field. */
export function requestStatusOf(request) {
  return request?.requestStatus ?? request?.status ?? "";
}

/**
 * Whether a request is worth opening.
 *
 * A request starts life as a code with nothing behind it: the reviewer reads
 * the verification code off the list and tells the candidate, and there is no
 * detail to see until the candidate has filled and submitted the form. Opening
 * one would show a page of dashes over an Approve button, and approving it
 * would onboard an employee whose details nobody has.
 *
 * Judged on the status rather than on a list of reviewable ones, so a status
 * added later is reviewable by default instead of silently unreachable.
 */
export function isReviewableRequest(request) {
  return !/code[_\s-]?generated/i.test(requestStatusOf(request));
}

/**
 * A request whose outcome is already recorded.
 *
 * Read off the status for the same reason the badge's tone is: the outcome is
 * spelled differently by different endpoints, and a wording added later should
 * read as decided rather than as still waiting.
 */
function isDecidedRequest(request) {
  return /onboard|approv|complet|reject|declin|cancel/i.test(requestStatusOf(request));
}

/**
 * The one thing the list offers on a request.
 *
 * A request with nothing behind it can only be closed, one already decided can
 * only be read back, and anything else is waiting on the reviewer to answer it.
 */
export function requestAction(request) {
  if (!isReviewableRequest(request)) return "reject";
  return isDecidedRequest(request) ? "view" : "review";
}

/**
 * The submitted half as an editable form state — every field present, so a
 * value the request came back without is an empty box rather than a missing
 * key, and the body sent later carries all of them either way.
 */
export function submittedPayload(request) {
  return SUBMITTED_FIELDS.reduce((body, key) => {
    body[key] = request?.[key] ?? "";
    return body;
  }, {});
}

/**
 * Fields the reviewer reads but must not retype.
 *
 * The verification code is generated for the candidate to quote back; editing
 * it here would change what they were told. It still travels in the update
 * body, unchanged.
 */
export function isReadOnlyField(key) {
  return key === "verificationCode";
}

/** The one submitted field that is a date rather than free text. */
export function isDateField(key) {
  return key === "dateOfBirth";
}

/** A shift as the picker names it: "General (09:00 - 18:00)". */
export function shiftLabel(shift) {
  const time = (value) => String(value ?? "").slice(11, 16);
  const start = time(shift?.startTime);
  const end = time(shift?.endTime);
  const hours = start && end ? ` (${start} - ${end})` : "";
  return `${shift?.name || shift?.id}${hours}`;
}
